import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Building2, Cpu, Check, Factory, Wrench, Cog, Boxes,
  Zap, Layers, LifeBuoy, MoveRight, Container, Download,
} from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "@/components/common/Reveal";
import { SectionHeading, Overline, CTAButton } from "@/components/common/Primitives";
import {
  IMAGES, ECOSYSTEM, INFRA_SERVICES, TECH_SERVICES, INDUSTRIES,
  GREENFIELD, BROWNFIELD, EXECUTION, WHY, MATERIAL_HANDLING, PROJECT_CATEGORIES, PROJECTS,
  COMPANY_PROFILE_PDF,
} from "@/data/site";

/* ---------------- WHO IS AUTOMEX ---------------- */
export function WhoIsAutomex() {
  const cards = [
    {
      tag: "AutoMex Infra Projects",
      icon: Building2,
      img: IMAGES.infra,
      logo: "/logos/automex-infra-stacked.png",
      items: ["Industrial Infrastructure", "Industrial Civil", "PEB & Structures", "Turnkey Projects", "Project Execution", "Machinery Integration"],
      cta: "Explore Infra Projects",
      to: "/infra-projects",
    },
    {
      tag: "AutoMex Industrial Technologies",
      icon: Cpu,
      img: IMAGES.tech,
      logo: "/logos/automex-tech.png",
      items: ["Industrial Machinery", "Automation", "Material Handling", "Industrial Equipment", "Technology Integration", "Industrial Solutions"],
      cta: "Explore Industrial Technologies",
      to: "/industrial-technologies",
    },
  ];
  return (
    <section className="py-24 md:py-32 bg-automex-mist bg-blueprint" data-testid="who-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <Reveal>
          <SectionHeading
            overline="Who Is AutoMex"
            title={<>One Industrial Platform.<br />Two Core Capabilities.</>}
            subtitle="AutoMex brings together industrial infrastructure, project execution, machinery, material handling and industrial technology under one integrated platform."
          />
        </Reveal>

        <div className="mt-16 grid gap-6 lg:grid-cols-2">
          {cards.map((c, idx) => (
            <Reveal key={c.tag} delay={idx * 0.1}>
              <div className="group relative flex h-full flex-col overflow-hidden rounded-sm border border-slate-200 bg-white">
                <div className="relative h-56 overflow-hidden">
                  <img src={c.img} alt={c.tag} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-automex-navy via-automex-navy/70 to-automex-navy/20" />
                  <div className="absolute bottom-0 left-0 flex items-center gap-3 p-6">
                    <span className="flex h-11 w-11 items-center justify-center bg-automex-teal text-automex-navy rounded-sm">
                      <c.icon className="h-5 w-5" />
                    </span>
                    <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-white">{c.tag}</h3>
                  </div>
                </div>
                <div className="flex flex-1 flex-col p-6 sm:p-8">
                  <img src={c.logo} alt={c.tag} className="h-12 w-auto object-contain mb-6 self-start" />
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {c.items.map((i) => (
                      <li key={i} className="flex items-center gap-2.5 text-sm text-slate-700">
                        <Check className="h-4 w-4 text-automex-teal shrink-0" />{i}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8 pt-6 border-t border-slate-100">
                    <Link to={c.to} className="group/link inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-automex-blue" data-testid={`who-cta-${idx}`}>
                      {c.cta}
                      <ArrowRight className="h-4 w-4 transition-transform group-hover/link:translate-x-1" />
                    </Link>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- ECOSYSTEM ---------------- */
export function Ecosystem() {
  return (
    <section className="py-24 md:py-32 bg-automex-mist text-automex-ink bg-blueprint overflow-hidden" data-testid="ecosystem-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <Reveal>
          <SectionHeading
            overline="AutoMex Ecosystem"
            title="From Infrastructure to Industrial Technology."
            subtitle="AutoMex connects the infrastructure of industry with the machinery and technologies that make it productive."
          />
        </Reveal>

        <div className="mt-16 grid gap-y-10 gap-x-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
          {ECOSYSTEM.map((step, i) => (
            <Reveal key={step} delay={i * 0.07}>
              <div className="relative flex flex-col items-start border-t-2 border-automex-blue/20 pt-5">
                <span className="font-heading text-5xl font-black text-automex-blue/15">{String(i + 1).padStart(2, "0")}</span>
                <span className="mt-2 font-heading text-lg font-bold uppercase tracking-tight text-automex-navy">{step}</span>
                {i < ECOSYSTEM.length - 1 && (
                  <MoveRight className="hidden lg:block absolute -right-3 top-8 h-5 w-5 text-automex-teal" aria-hidden="true" />
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <div className="mt-20 border-y border-slate-200 py-5 overflow-hidden">
        <div className="flex whitespace-nowrap animate-marquee">
          {[0, 1].map((k) => (
            <div key={k} className="flex items-center gap-8 pr-8" aria-hidden={k === 1}>
              {["Plan", "Build", "Equip", "Automate", "Integrate", "Commission", "Plan", "Build", "Equip", "Automate", "Integrate", "Commission"].map((w, i) => (
                <span key={i} className="flex items-center gap-8 font-heading text-3xl md:text-4xl font-black uppercase tracking-tight text-automex-blue">
                  {w}<span className="text-automex-teal text-2xl">◆</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- CAPABILITY SHOWCASE (image + copy) ---------------- */
export function CapabilityShowcase({ reverse, overline, title, desc, services, cta, ctaTo, image, imageAlt }) {
  return (
    <section className="py-24 md:py-32 bg-white" data-testid={`showcase-${overline.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className={`grid gap-12 lg:grid-cols-2 lg:gap-16 items-center ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}>
          <Reveal>
            <div className="relative">
              <div className="overflow-hidden rounded-sm border border-slate-200">
                <img src={image} alt={imageAlt} loading="lazy" className="aspect-[4/3] w-full object-cover" />
              </div>
              <div className="absolute -bottom-5 -left-5 hidden sm:block h-24 w-24 border-l-2 border-b-2 border-automex-teal" aria-hidden="true" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Overline>{overline}</Overline>
            <h2 className="mt-4 font-heading text-4xl sm:text-5xl font-bold uppercase tracking-tight leading-[0.95] text-automex-ink">{title}</h2>
            <p className="mt-5 text-slate-600 leading-relaxed">{desc}</p>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {services.map((s) => (
                <li key={s} className="group/item relative flex items-center gap-3 overflow-hidden rounded-md border border-slate-200 bg-white py-3 pl-4 pr-3 text-sm font-semibold text-automex-navy transition-all duration-300 hover:-translate-y-0.5 hover:border-automex-teal hover:shadow-md">
                  <span className="absolute left-0 top-0 h-full w-1 origin-top scale-y-0 bg-automex-teal transition-transform duration-300 group-hover/item:scale-y-100" />
                  <span className="flex h-8 w-8 items-center justify-center rounded-md bg-gradient-to-br from-automex-navy to-automex-blue text-automex-teal shrink-0">
                    <Check className="h-4 w-4" />
                  </span>
                  <span className="flex-1 leading-tight">{s}</span>
                  <ArrowRight className="h-4 w-4 shrink-0 -translate-x-1 text-automex-teal opacity-0 transition-all duration-300 group-hover/item:translate-x-0 group-hover/item:opacity-100" />
                </li>
              ))}
            </ul>
            <div className="mt-10">
              <CTAButton to={ctaTo} variant="blue">{cta}</CTAButton>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export function InfraShowcase() {
  return (
    <CapabilityShowcase
      overline="Infra Projects"
      title={<>From Factory Plan<br />to Production.</>}
      desc="AutoMex Infra Projects provides coordinated industrial infrastructure and project execution solutions for manufacturing facilities, expansions and industrial developments."
      services={["Industrial Civil & Infrastructure", "PEB & Industrial Structures", "Turnkey Industrial Projects", "Factory Expansion", "Brownfield Projects", "Machinery Installation", "Electrical & Industrial Utilities", "Material Handling Integration"]}
      cta="Explore Infra Projects"
      ctaTo="/infra-projects"
      image={IMAGES.peb}
      imageAlt="Industrial steel structure under construction for a manufacturing facility"
    />
  );
}

export function TechShowcase() {
  return (
    <CapabilityShowcase
      reverse
      overline="Industrial Technologies"
      title={<>Technology That Moves<br />Industry Forward.</>}
      desc="AutoMex Industrial Technologies delivers machinery, automation, material handling and industrial equipment — integrated with your infrastructure and production goals."
      services={["Industrial Machinery", "Automation", "Material Handling", "Industrial Equipment", "Machine Integration", "Technology Solutions", "Industrial Components", "After-Sales Support"]}
      cta="Explore Industrial Technologies"
      ctaTo="/industrial-technologies"
      image={IMAGES.tech}
      imageAlt="Blue industrial robotic arm operating in a modern automated factory"
    />
  );
}

/* ---------------- DIFFERENTIATOR ---------------- */
export function Differentiator() {
  const chips = ["Factory", "Machinery", "Crane", "Automation", "Production"];
  return (
    <section className="relative overflow-hidden bg-automex-mist" data-testid="differentiator-section">
      <div className="absolute inset-0">
        <img src={IMAGES.crane} alt="" aria-hidden="true" className="h-full w-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-r from-automex-mist via-automex-mist/85 to-automex-navy/30" />
      </div>
      <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8 py-28 md:py-40">
        <Reveal>
          <Overline>The AutoMex Difference</Overline>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-heading font-black uppercase tracking-tighter leading-[0.9] text-4xl sm:text-6xl md:text-7xl max-w-4xl text-automex-navy">
            We don't just build the factory.{" "}
            <span className="text-automex-blue">We build for what happens inside it.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-2xl text-lg text-slate-700 leading-relaxed">
            Every industrial facility exists for production. AutoMex approaches infrastructure with
            machinery, material flow, utilities and operational requirements in mind.
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-12 flex flex-wrap items-center gap-3">
            {chips.map((c, i) => (
              <span key={c} className="flex items-center gap-3">
                <span className="border border-automex-blue/30 bg-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider text-automex-navy">{c}</span>
                {i < chips.length - 1 && <span className="text-automex-teal text-lg">+</span>}
              </span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- INDUSTRIES GRID ---------------- */
export function IndustriesGrid({ withHeading = true }) {
  return (
    <section className="py-24 md:py-32 bg-automex-mist" data-testid="industries-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        {withHeading && (
          <Reveal>
            <SectionHeading
              overline="Industries We Serve"
              title="Engineered for Every Manufacturing Sector."
              subtitle="AutoMex supports a broad range of industrial and manufacturing sectors with infrastructure and technology solutions."
            />
          </Reveal>
        )}
        <Stagger className="mt-16 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
          {INDUSTRIES.map((ind) => (
            <StaggerItem key={ind.name}>
              <div className="group relative h-40 sm:h-48 overflow-hidden bg-automex-navy">
                <img src={ind.img} alt={`${ind.name} industry`} loading="lazy" className="h-full w-full object-cover opacity-60 transition-all duration-700 group-hover:opacity-75 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-transparent" />
                <div className="absolute inset-0 flex items-end p-5">
                  <span className="font-heading text-base sm:text-lg font-bold uppercase tracking-tight text-white leading-tight">{ind.name}</span>
                </div>
                <span className="absolute top-0 left-0 h-0.5 w-0 bg-automex-teal transition-all duration-500 group-hover:w-full" />
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

/* ---------------- GREENFIELD / BROWNFIELD ---------------- */
export function GreenBrownfield() {
  const blocks = [
    { tag: "Greenfield", label: "Built for New Factories", items: GREENFIELD, img: IMAGES.architecture, accent: true },
    { tag: "Brownfield", label: "Ready for Existing Ones", items: BROWNFIELD, img: IMAGES.productionLine, accent: false },
  ];
  return (
    <section className="py-24 md:py-32 bg-white" data-testid="greenbrownfield-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <Reveal>
          <SectionHeading
            overline="Greenfield & Brownfield"
            title={<>Built for New Factories.<br />Ready for Existing Ones.</>}
          />
        </Reveal>
        <div className="mt-16 grid gap-6 lg:grid-cols-2">
          {blocks.map((b, idx) => (
            <Reveal key={b.tag} delay={idx * 0.1}>
              <div className="group relative h-full overflow-hidden rounded-sm border border-slate-200">
                <div className="relative h-44 overflow-hidden">
                  <img src={b.img} alt={`${b.tag} industrial projects`} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-r from-automex-navy/85 via-automex-navy/55 to-automex-navy/25" />
                  <div className="absolute inset-0 flex flex-col justify-center p-8">
                    <span className={`text-xs font-bold uppercase tracking-[0.2em] ${b.accent ? "text-automex-teal" : "text-white/80"}`}>{b.tag}</span>
                    <h3 className="mt-2 font-heading text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white">{b.label}</h3>
                  </div>
                </div>
                <div className="p-8 bg-white">
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {b.items.map((i) => (
                      <li key={i} className="flex items-center gap-2.5 text-sm text-slate-700">
                        <Check className="h-4 w-4 text-automex-teal shrink-0" />{i}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- EXECUTION TIMELINE ---------------- */
export function ExecutionTimeline() {
  return (
    <section className="py-24 md:py-32 bg-white bg-blueprint" data-testid="execution-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <Reveal>
          <SectionHeading overline="Project Execution" title="A Structured Path to Production." />
        </Reveal>
        <div className="mt-16 grid gap-px sm:grid-cols-2 lg:grid-cols-6 bg-slate-200 border border-slate-200">
          {EXECUTION.map((step, i) => (
            <Reveal key={step.no} delay={i * 0.06}>
              <div className="group relative h-full bg-white p-7 transition-colors hover:bg-automex-navy">
                <span className="font-heading text-4xl font-black text-automex-blue group-hover:text-automex-teal">{step.no}</span>
                <h3 className="mt-4 font-heading text-lg font-bold uppercase tracking-tight text-automex-ink group-hover:text-white">{step.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed group-hover:text-slate-300">{step.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- WHY AUTOMEX ---------------- */
export function WhyAutomex() {
  const icons = [Layers, Cog, Wrench, Factory, Zap, Boxes];
  return (
    <section className="py-24 md:py-32 bg-automex-mist bg-blueprint" data-testid="why-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <Reveal>
          <SectionHeading
            overline="Why AutoMex"
            title={<>Engineering-Led.<br />Execution-Focused.<br />Technology-Ready.</>}
          />
        </Reveal>
        <div className="mt-16 grid gap-px sm:grid-cols-2 lg:grid-cols-3 bg-slate-200 border border-slate-200">
          {WHY.map((w, i) => {
            const Icon = icons[i % icons.length];
            return (
              <Reveal key={w.title} delay={i * 0.06}>
                <div className="group h-full bg-white p-8 transition-colors hover:bg-automex-navy">
                  <Icon className="h-8 w-8 text-automex-teal" />
                  <h3 className="mt-5 font-heading text-lg font-bold uppercase tracking-tight text-automex-ink group-hover:text-white">{w.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed group-hover:text-slate-300">{w.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------------- MATERIAL HANDLING ---------------- */
export function MaterialHandling() {
  return (
    <section className="py-24 md:py-32 bg-white" data-testid="material-handling-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-center">
          <Reveal>
            <Overline>Material Handling</Overline>
            <h2 className="mt-4 font-heading text-4xl sm:text-5xl font-bold uppercase tracking-tight leading-[0.95] text-automex-ink">
              Move Material.<br /><span className="text-automex-blue">Move Production.</span>
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Material handling can be integrated directly with AutoMex industrial infrastructure projects —
              engineered into the structure, utilities and layout from day one.
            </p>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {MATERIAL_HANDLING.map((m) => (
                <li key={m} className="flex items-center gap-2.5 text-sm text-slate-700">
                  <Container className="h-4 w-4 text-automex-teal shrink-0" />{m}
                </li>
              ))}
            </ul>
            <p className="mt-6 text-xs text-slate-500">
              Delivered in association with <span className="font-semibold text-automex-blue">LiftoMech</span>, the associated material-handling &amp; crane brand.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative">
              <div className="overflow-hidden rounded-sm border border-slate-200">
                <img src={IMAGES.craneYellow} alt="Overhead EOT crane system installed in an industrial facility" loading="lazy" className="aspect-[4/3] w-full object-cover" />
              </div>
              <div className="absolute -top-5 -right-5 hidden sm:block h-24 w-24 border-t-2 border-r-2 border-automex-teal" aria-hidden="true" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------------- PROJECTS GRID (with filter) ---------------- */
export function ProjectsGrid({ withHeading = true }) {
  const [active, setActive] = useState("All");
  const cats = ["All", ...PROJECT_CATEGORIES];
  const filtered = active === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === active);
  return (
    <section className="py-24 md:py-32 bg-automex-mist" data-testid="projects-section">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        {withHeading && (
          <Reveal>
            <SectionHeading
              overline="Projects"
              title="Capabilities in Action."
              subtitle="Representative project types across our infrastructure and technology capabilities. Detailed case studies will be published here."
            />
          </Reveal>
        )}
        <Reveal delay={0.05}>
          <div className="mt-10 flex flex-wrap gap-2" data-testid="project-filters">
            {cats.map((c) => (
              <button
                key={c}
                onClick={() => setActive(c)}
                data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-sm border transition-colors ${
                  active === c ? "bg-automex-blue text-white border-automex-blue" : "bg-white text-slate-600 border-slate-200 hover:border-automex-blue"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </Reveal>
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p, i) => (
            <motion.div
              key={p.title}
              layout
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.05 }}
              className="group flex flex-col overflow-hidden rounded-sm border border-slate-200 bg-white"
            >
              <div className="relative h-48 overflow-hidden">
                <img src={p.img} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <span className="absolute top-4 left-4 bg-automex-navy text-white px-3 py-1 text-[10px] font-bold uppercase tracking-wider">{p.category}</span>
              </div>
              <div className="flex flex-1 flex-col p-6">
                <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-automex-ink leading-tight">{p.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.scope}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- SERVICES GRID (for capability pages) ---------------- */
export function ServicesGrid({ services, columns = 4 }) {
  const icons = [Building2, Factory, Wrench, Cog, Boxes, Zap, Layers, LifeBuoy];
  const colClass = columns === 4 ? "lg:grid-cols-4" : "lg:grid-cols-3";
  return (
    <div className={`grid gap-px sm:grid-cols-2 ${colClass} bg-slate-200 border border-slate-200`}>
      {services.map((s, i) => {
        const Icon = icons[i % icons.length];
        return (
          <Reveal key={s.title} delay={(i % 4) * 0.05}>
            <div className="group h-full bg-white p-7 transition-colors hover:bg-automex-navy">
              <Icon className="h-7 w-7 text-automex-teal" />
              <h3 className="mt-5 font-heading text-base font-bold uppercase tracking-tight text-automex-ink group-hover:text-white">{s.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed group-hover:text-slate-300">{s.desc}</p>
            </div>
          </Reveal>
        );
      })}
    </div>
  );
}

/* ---------------- CONTACT BAND ---------------- */
export function ContactBand() {
  return (
    <section className="relative overflow-hidden bg-automex-teal" data-testid="contact-band">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-20 md:py-24">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <Reveal>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-automex-navy/70">Start a Conversation</div>
            <h2 className="mt-4 font-heading text-4xl sm:text-5xl md:text-6xl font-black uppercase tracking-tighter leading-[0.9] text-automex-navy">
              Planning Your Next<br />Industrial Project?
            </h2>
            <p className="mt-5 max-w-xl text-automex-navy/80 leading-relaxed">
              Tell us what you are building, expanding or automating. Our team will help you plan the right path.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/contact"
                data-testid="band-discuss-cta"
                className="group inline-flex items-center justify-center gap-2.5 bg-automex-navy text-white px-8 py-4 text-sm font-bold uppercase tracking-wider rounded-sm hover:bg-automex-ink transition-colors"
              >
                Discuss Your Requirement
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href={COMPANY_PROFILE_PDF.url}
                download={COMPANY_PROFILE_PDF.filename}
                data-testid="band-profile-cta"
                className="group inline-flex items-center justify-center gap-2.5 border-2 border-automex-navy text-automex-navy px-8 py-4 text-sm font-bold uppercase tracking-wider rounded-sm hover:bg-automex-navy hover:text-white transition-colors"
              >
                <Download className="h-4 w-4" />
                Download Company Profile
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
